import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { entriesApi } from '../../../../api/entries';
import { exportsApi, saveBlob, ExportError } from '../../../../api/exports';
import { projectsApi } from '../../../../api/projects';
import { Project, PermissionEnum } from '../../../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { cn, formatDate } from '../../../../lib/utils';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/molecules/table';
import { CollapsibleBandRow, ExpandCollapseAll } from '../../../../components/ui/molecules/collapsible-band';
import { useCollapsibleGroups } from '../../../../lib/useCollapsibleGroups';
import { SearchableSelect } from '../../../../components/ui/molecules/searchable-select';
import { UserCombobox } from '../../../../components/ui/molecules/user-combobox';
import { Loading } from '../components';
import { parseWeekFilter } from '../utils';

export function DangerZoneTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [from, setFrom] = useState(`${currentYear}-01-01`);
  const [to, setTo] = useState(`${currentYear}-12-31`);
  const [weekFilter, setWeekFilter] = useState('');
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const { hasPermission } = useAuth();
  const canExport = hasPermission(PermissionEnum.EXPORT_DATA);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');

  const changeYear = (newYear: number) => {
    setYear(newYear);
    setFrom(`${newYear}-01-01`);
    setTo(`${newYear}-12-31`);
    setWeekFilter('');
  };

  const handleWeekFilter = (val: string) => {
    setWeekFilter(val);
    const range = parseWeekFilter(val, year);
    if (!range) return;
    setFrom(range.from);
    setTo(range.to);
  };

  const reset = () => {
    setWeekFilter('');
    setFrom(`${currentYear}-01-01`);
    setTo(`${currentYear}-12-31`);
    setYear(currentYear);
    setUserId('');
    setProjectId('');
  };

  // The backend works out who is off target (including people who logged nothing), so this
  // list and the export are the same calculation.
  const { data, isLoading } = useQuery({
    queryKey: ['danger-zone', from, to, userId, projectId],
    queryFn: () =>
      entriesApi.adminDangerZone({
        from: from || undefined,
        to: to || undefined,
        userId: userId || undefined,
        projectId: projectId || undefined,
      }).then((r) => r.data),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
  });

  const projects: Project[] = projectsData ?? [];
  const dangerWeeks = data?.weeks ?? [];
  const groups = useCollapsibleGroups(dangerWeeks.map((w) => w.dateRange.from));

  const hasFilters = weekFilter || userId || projectId;

  // The backend builds the file from the same filters the table above uses.
  const handleExport = async () => {
    if (dangerWeeks.length === 0) return;
    setExportMessage('');
    setIsExporting(true);
    try {
      const file = await exportsApi.dangerZone({ from, to, userId, projectId });
      saveBlob(file.blob, file.filename);
    } catch (error) {
      setExportMessage(error instanceof ExportError ? error.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Week #</label>
          <Input
            type="number"
            min={1}
            max={53}
            placeholder="e.g. 25"
            value={weekFilter}
            onChange={(e) => handleWeekFilter(e.target.value)}
            className="w-24"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <Input
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setWeekFilter(''); }}
            className="w-36"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <Input
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setWeekFilter(''); }}
            className="w-36"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Employee</label>
          <UserCombobox
            value={userId}
            onValueChange={setUserId}
            allowEmpty
            emptyLabel="All employees"
            placeholder="All employees"
            className="w-44"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Project</label>
          <SearchableSelect
            value={projectId}
            onValueChange={setProjectId}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            allowEmpty
            emptyLabel="All projects"
            placeholder="All projects"
            searchPlaceholder="Search projects..."
            className="w-44"
          />
        </div>
        <div className="flex items-end gap-1">
          <Button variant="outline" size="sm" onClick={() => changeYear(year - 1)}>&#8249;</Button>
          <span className="text-sm font-medium w-12 text-center pb-1">{year}</span>
          <Button variant="outline" size="sm" disabled={year >= currentYear} onClick={() => changeYear(year + 1)}>&#8250;</Button>
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={reset}>Clear</Button>
        )}
        <div className="flex items-end gap-2 ml-auto">
          {exportMessage && <span className="pb-2 text-xs text-muted-foreground">{exportMessage}</span>}
          <ExpandCollapseAll
            allExpanded={groups.allExpanded}
            onExpandAll={groups.expandAll}
            onCollapseAll={groups.collapseAll}
            disabled={dangerWeeks.length === 0}
          />
          {canExport && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={isExporting || isLoading || dangerWeeks.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          )}
        </div>
      </div>

      {data && (
        <p className="text-xs text-muted-foreground">
          Checks everyone with the Log Hours permission from {formatDate(`${data.startsOn}T00:00:00`)}, including people
          who logged nothing. A week&apos;s target is 8h per Sunday–Thursday day, so the first and the current
          week are partial.
        </p>
      )}

      {isLoading ? (
        <Loading />
      ) : dangerWeeks.length === 0 ? (
        <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-200 dark:border-slate-800">
          <p className="text-sm text-muted-foreground">
            {data?.period === null
              ? `Nothing to check: the Danger Zone starts on ${formatDate(`${data.startsOn}T00:00:00`)}.`
              : 'All weeks are clean. Everyone logged exactly their target hours.'}
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Total Hours</TableHead>
              <TableHead>vs target</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dangerWeeks.map(({ week, dateRange, target, users: weekUsers }) => (
              <React.Fragment key={dateRange.from}>
                <CollapsibleBandRow
                  colSpan={3}
                  expanded={groups.isExpanded(dateRange.from)}
                  onToggle={() => groups.toggle(dateRange.from)}
                >
                  <span className="font-semibold text-sm">Week {week}</span>
                  <span className="ml-3 text-xs opacity-70">
                    {formatDate(dateRange.from + 'T00:00:00')} – {formatDate(dateRange.to + 'T00:00:00')}
                  </span>
                  <span className="ml-3 text-xs opacity-70">{weekUsers.length} employee{weekUsers.length !== 1 ? 's' : ''} flagged</span>
                  <span className="ml-3 text-xs opacity-70">target {target}h</span>
                </CollapsibleBandRow>
                {groups.isExpanded(dateRange.from) && weekUsers.map((u) => (
                  <TableRow key={u.userId}>
                    <TableCell>
                      <div className="font-medium pl-4">{u.name}</div>
                      <div className="text-xs text-muted-foreground pl-4">{u.email}</div>
                    </TableCell>
                    <TableCell>{u.total}h</TableCell>
                    <TableCell>
                      <span className={cn(
                        'font-medium tabular-nums',
                        u.diff > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-destructive',
                      )}>
                        {u.diff > 0 ? `+${u.diff}h` : `${u.diff}h`}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
